/**
 * Fee Dues helpers — account-level rows aligned with Parent Accounts pending.
 */
import { todayString } from './appDateTime';

/**
 * Derive display status from next_due_date.
 * @returns {'Overdue'|'Due soon'}
 */
export function deriveDuesStatus(nextDueDate, todayStr = todayString()) {
  if (!nextDueDate) return 'Overdue';
  return String(nextDueDate).slice(0, 10) >= todayStr ? 'Due soon' : 'Overdue';
}

/**
 * Whether a parent account should appear on Fee Dues (any pending balance).
 */
export function isAccountPendingForDues(account) {
  const pending = Number(account?.total_pending ?? account?.pending_amount ?? 0);
  return pending > 0;
}

/** @deprecated Use isAccountPendingForDues */
export function isAccountOverdueForDues(account, todayStr) {
  return isAccountPendingForDues(account);
}

/**
 * Filter and normalize parent account rows for Fee Dues table.
 * @param {Array} accounts - GET /fees/parent-accounts or overdue-dues
 * @returns {Array}
 */
export function filterAndNormalizeDuesAccounts(accounts = []) {
  const today = todayString();

  return (accounts || [])
    .filter((acc) => isAccountPendingForDues(acc))
    .map((acc) => {
      const pending = Number(acc.total_pending ?? acc.pending_amount ?? 0);
      const nextDue = acc.next_due_date ?? null;
      const status = acc.status || deriveDuesStatus(nextDue, today);
      return {
        parent_account_id: Number(acc.parent_account_id ?? acc.id),
        parent_name: acc.parent_name ?? acc.name ?? 'Parent/Guardian',
        parent_email: acc.parent_email ?? acc.email ?? null,
        parent_mobile: acc.parent_mobile ?? acc.mobile ?? null,
        student_count: acc.student_count ?? 0,
        total_yearly: acc.total_yearly ?? 0,
        total_pending: pending,
        pending_amount: pending,
        next_due_date: nextDue,
        status,
      };
    })
    .sort((a, b) => {
      const aDue = a.next_due_date ? String(a.next_due_date).slice(0, 10) : null;
      const bDue = b.next_due_date ? String(b.next_due_date).slice(0, 10) : null;
      if (!aDue && !bDue) {
        return String(a.parent_name).localeCompare(String(b.parent_name));
      }
      if (!aDue) return 1;
      if (!bDue) return -1;
      if (aDue !== bDue) return aDue.localeCompare(bDue);
      return String(a.parent_name).localeCompare(String(b.parent_name));
    });
}

/**
 * Client-side search on parent account dues rows.
 */
export function filterDuesBySearch(rows, search = '') {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => {
    const hay = [
      row.parent_name,
      row.parent_mobile,
      row.parent_email,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}

/**
 * Extract API error message from axios error (JSON or HTML body).
 */
export function getApiErrorMessage(error, fallback = 'Request failed') {
  if (!error) return fallback;
  if (!error.response) {
    return error.message || 'Cannot reach API server. Check network and REACT_APP_BACKEND_URL.';
  }
  const data = error.response.data;
  if (typeof data === 'string') {
    if (data.includes('Page Not Found')) {
      return 'API endpoint not found (404). Deploy latest backend.';
    }
    return fallback;
  }
  if (data?.message) return data.message;
  return fallback;
}

export function isNotFoundError(error) {
  return error?.response?.status === 404;
}
